import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiSun, FiMoon, FiMessageCircle, FiMenu, FiX, FiChevronUp } from 'react-icons/fi';
import { FaFacebook, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { useTranslation } from '../hooks/useTranslation';
import LanguageToggle from './LanguageToggle';
import FloatingChatBot from './FloatingChatBot';

const Layout = ({ children, isDarkMode, setIsDarkMode }) => {
  const { t, language } = useTranslation();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navItems = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.portfolio'), href: '#portfolio' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  // Handle navbar hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 100) {
        // Always show navbar at the top
        setShowNavbar(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling DOWN - hide navbar
        setShowNavbar(false);
        setIsMobileMenuOpen(false); // Also close mobile menu if open
      } else {
        // Scrolling UP - show navbar
        setShowNavbar(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuButton = document.getElementById('mobile-menu-button');
      const mobileMenu = document.getElementById('mobile-menu');
      
      if (isMobileMenuOpen && 
          mobileMenu && 
          !mobileMenu.contains(event.target) && 
          menuButton && 
          !menuButton.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Handle smooth scrolling for anchor links
  const handleNavClick = (href, e) => {
    if (e) e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const offset = 80; // Adjust for navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    
    // Close mobile menu after clicking
    setIsMobileMenuOpen(false);
  };

  // Handle consultation button click
  const handleConsultationClick = () => {
    handleNavClick('#contact');
  };

  return (
    <div className="min-h-screen bg-[#FFFDD0] dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      {/* Header with scroll hide/show */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ 
          y: showNavbar ? 0 : -100,
          opacity: showNavbar ? 1 : 0
        }}
        transition={{ 
          duration: 0.3,
          ease: "easeInOut"
        }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-lg"
      >
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo with Image */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3"
            >
              {/* Logo Image */}
              <div className="flex items-center">
                <img 
                  src="/logo.png" 
                  alt="M.A.S Elite Logo" 
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallbackDiv = document.createElement('div');
                    fallbackDiv.className = 'flex items-center space-x-3';
                    fallbackDiv.innerHTML = `
                      <div class="w-10 h-10 bg-gradient-to-br from-amber-600 to-amber-800 rounded-lg flex items-center justify-center">
                        <span class="text-white font-bold text-xl">M</span>
                      </div>
                      <div>
                        <h1 class="text-xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
                          ${t('header.companyName')}
                        </h1>
                        <p class="text-xs text-gray-600 dark:text-gray-400">${t('header.tagline')}</p>
                      </div>
                    `;
                    e.target.parentNode.appendChild(fallbackDiv);
                  }}
                />
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(item.href, e)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 font-medium transition-colors relative group"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-600 group-hover:w-full transition-all duration-300"></span>
                </motion.a>
              ))}
            </nav>

            {/* Right side controls */}
            <div className="flex items-center space-x-4">
              {/* Chatbot Button in Navbar */}
              <motion.button
                onClick={() => setIsChatbotOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:shadow-lg transition-all nav-chatbot-btn"
                aria-label={language === 'ar' ? 'المساعد الذكي' : 'AI Assistant'}
              >
                <FiMessageCircle size={18} />
                <span className="hidden lg:inline">
                  {language === 'ar' ? 'المساعد الذكي' : 'AI Assistant'}
                </span>
              </motion.button>

              {/* Language Toggle */}
              <div className="hidden md:block">
                <LanguageToggle />
              </div>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
              </button>
              
              {/* Consultation Button - Desktop only */}
              <motion.button
                onClick={handleConsultationClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="hidden md:block px-6 py-2 bg-gradient-to-r from-[#A37A00] to-amber-800 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                {t('header.consultation')}
              </motion.button>

              {/* Mobile Menu Button */}
              <button
                id="mobile-menu-button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg"
            >
              <div className="container mx-auto px-6 py-4">
                {/* Mobile Navigation Links */}
                <div className="space-y-3 mb-6">
                  {navItems.map((item) => (
                    <motion.a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleNavClick(item.href, e)}
                      whileTap={{ scale: 0.95 }}
                      className="block py-3 px-4 text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-medium transition-colors"
                    >
                      {item.name}
                    </motion.a>
                  ))}
                </div>

                {/* Mobile Language Toggle */}
                <div className="mb-4">
                  <LanguageToggle />
                </div>

                {/* Mobile Consultation Button */}
                <motion.button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleConsultationClick();
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full py-3 bg-gradient-to-r from-[#A37A00] to-amber-800 text-white rounded-lg font-medium hover:shadow-lg transition-shadow mb-4"
                >
                  {t('header.consultation')}
                </motion.button>

                {/* Mobile Contact Info */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    <div className="flex items-center gap-2">
                      <FiPhone size={16} />
                      <span>+20 1270 328 820</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiMail size={16} />
                      <a 
                        href="mailto:mohamedabosriea@masellite.com" 
                        className="hover:text-amber-600 dark:hover:text-amber-400"
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = 'mailto:mohamedabosriea@masellite.com';
                        }}
                      >
                        mohamedabosriea@masellite.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Scroll to top button */}
      <AnimatePresence>
        {lastScrollY > 300 && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 right-6 z-40 p-3 bg-gradient-to-r from-[#A37A00] to-amber-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
            aria-label="Scroll to top"
          >
            <FiChevronUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info with Logo */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                {/* Footer Logo - Smaller version */}
                <img 
                  src="/logo.png" 
                  alt="M.A.S Elite Logo" 
                  className="h-10 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallbackDiv = document.createElement('div');
                    fallbackDiv.className = 'w-8 h-8 bg-gradient-to-br from-amber-600 to-amber-800 rounded-lg flex items-center justify-center';
                    fallbackDiv.innerHTML = '<span class="text-white font-bold">M</span>';
                    e.target.parentNode.appendChild(fallbackDiv);
                  }}
                />
                <h3 className="text-2xl font-bold">{t('header.companyName')}</h3>
              </div>
              <p className="text-gray-400 mb-4">
                {t('footer.description')}
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors" aria-label="Facebook">
                  <FaFacebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors" aria-label="LinkedIn">
                  <FaLinkedin size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-amber-400 transition-colors" aria-label="Twitter">
                  <FaTwitter size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <a 
                      href={item.href} 
                      onClick={(e) => handleNavClick(item.href, e)}
                      className="text-gray-400 hover:text-amber-400 transition-colors"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.services')}</h4>
              <ul className="space-y-2 text-gray-400">
                {t('footer.serviceList').map((service, index) => (
                  <li key={index} className="hover:text-white transition-colors cursor-pointer">
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.contactUs')}</h4>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <FiPhone className="text-amber-400" />
                  <span className="text-gray-400 hover:text-white transition-colors">
                    +20 1270 328 820
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <FiMail className="text-amber-400" />
                  <a 
                    href="mailto:mohamedabosriea@masellite.com" 
                    className="text-gray-400 hover:text-white transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = 'mailto:mohamedabosriea@masellite.com';
                    }}
                  >
                    mohamedabosriea@masellite.com
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <FiMapPin className="text-amber-400" />
                  <span className="text-gray-400 hover:text-white transition-colors">
                    Sheikh Zayed City, Giza, Egypt
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Floating Chatbot */}
      <FloatingChatBot 
        isOpen={isChatbotOpen}
        onOpen={() => setIsChatbotOpen(true)}
        onClose={() => setIsChatbotOpen(false)}
      />
    </div>
  );
};

export default Layout;